import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, AlertTriangle, ExternalLink } from "lucide-react";
import axios from "axios";

// Public-facing magic-link page. Reps land here from email — we immediately
// POST the requested action (no confirmation page per spec choice "C"), then
// show the outcome with a tiny CTA back to the marketing site.

const STAGE_LABEL = {
  contacted: "Contacted",
  qualified: "Discovery",
  quote_sent: "Quote Sent",
  negotiation: "Negotiation",
  won: "Won",
  lost: "Lost",
};

export default function LeadActionPage() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const action = search.get("action");

  const [state, setState] = useState({ phase: "loading", data: null, error: null });
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;

    (async () => {
      if (!token || !action) {
        setState({ phase: "error", error: "Missing token or action in URL.", data: null });
        return;
      }
      try {
        // Verify first so we can display the lead label/result even if
        // the apply step fails or token is in a non-active state.
        const verify = await axios.get(`/api/lead-action/${token}/verify`);
        if (verify.data?.status === "used") {
          setState({
            phase: "already_used",
            data: { ...verify.data, action: verify.data.used_action },
            error: null,
          });
          return;
        }
        if (verify.data?.status === "expired") {
          setState({ phase: "expired", data: verify.data, error: null });
          return;
        }
        // Token active → immediately apply.
        const apply = await axios.post(`/api/lead-action/apply`, { token, action });
        setState({ phase: "success", data: apply.data, error: null });
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Something went wrong.";
        const code = e?.response?.status;
        let phase = "error";
        if (code === 409) phase = "already_used";
        if (code === 410) phase = "expired";
        setState({ phase, error: msg, data: null });
      }
    })();
  }, [token, action]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="lead-action-page">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="mb-3 text-slate-500 text-xs uppercase tracking-wider">Portico Lead Update</div>

        {state.phase === "loading" && (
          <div className="flex flex-col items-center gap-3 py-6" data-testid="lead-action-loading">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            <p className="text-sm text-slate-500">Recording your update…</p>
          </div>
        )}

        {state.phase === "success" && (
          <div data-testid="lead-action-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">All set</h1>
            <p className="mt-2 text-slate-700">
              You marked <strong>{state.data?.lead_name || "the lead"}</strong>
              {state.data?.lead_company ? <> at <strong>{state.data.lead_company}</strong></> : null} as{" "}
              <span className="inline-flex px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-sm font-medium">
                {STAGE_LABEL[state.data?.new_status] || state.data?.new_status}
              </span>.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Thanks — sales ops has been notified automatically.
            </p>
          </div>
        )}

        {state.phase === "already_used" && (
          <div data-testid="lead-action-used">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Already updated</h1>
            <p className="mt-2 text-slate-700">
              This link was already used
              {state.data?.action ? <> — the lead is set to <strong>{STAGE_LABEL[state.data.action] || state.data.action}</strong></> : null}.
              Sales ops can send a new link if anything's changed.
            </p>
          </div>
        )}

        {state.phase === "expired" && (
          <div data-testid="lead-action-expired">
            <XCircle className="h-12 w-12 text-slate-400 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Link expired</h1>
            <p className="mt-2 text-slate-700">
              This update link is more than 7 days old. Reply to the original email and we'll send a fresh one.
            </p>
          </div>
        )}

        {state.phase === "error" && (
          <div data-testid="lead-action-error">
            <XCircle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Hmm, that didn't work</h1>
            <p className="mt-2 text-slate-600 text-sm">{state.error || "Unknown error."}</p>
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-slate-100 text-sm">
          <Link
            to="/"
            className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
            data-testid="lead-action-home-link"
          >
            <ExternalLink className="h-3.5 w-3.5" /> portico systems
          </Link>
        </div>
      </div>
    </div>
  );
}
